import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import dayjs from 'dayjs'
import useLocaleText from 'components/useLocaleText'

export type Item = { id: string; value: string }

const genId = (() => {
  let i = 0
  return () => `it_${i++}`
})()

const calcMaxItems = (fromDate: string, toDate: string): number => {
  if (!fromDate || !toDate) return 12902
  const from = dayjs(fromDate),
    to = dayjs(toDate)
  if (!from.isValid() || !to.isValid() || to.isBefore(from)) return 12902
  const days = to.diff(from, 'day') + 1
  if (days <= 15) return 12902
  if (days <= 31) return 6451
  if (days <= 62) return 3225
  return Math.floor(12902 / (days / 15))
}

export function useItemIds(fieldPath = 'products_cluster_data.item') {
  const { watch, setValue, register, triggerValidation, getValues, clearError } = useFormContext()

  const [items, setItems] = useState<Item[]>([])
  const hydratedRef = useRef(false)

  const t_add_at_least_one_id = useLocaleText('add_at_least_one_id', true)
  const t_max_allowed = useLocaleText('max_allowed', true)
  const t_you_have = useLocaleText('you_have', true)

  useEffect(() => {
    register(
      { name: fieldPath },
      {
        validate: () => {
          const arr = (getValues(fieldPath) || []) as Item[]
          const nonEmpty = arr.filter((i) => (i.value || '').trim())
          if (nonEmpty.length === 0) return t_add_at_least_one_id
          const from = getValues('products_cluster_data.app_from_ts') as string
          const to = getValues('products_cluster_data.to_ts') as string
          const max = calcMaxItems(from, to)
          return nonEmpty.length <= max || `${t_max_allowed} ${max}, ${t_you_have} ${nonEmpty.length}`
        },
      }
    )
  }, [register, getValues, fieldPath])

  const formRaw = watch(fieldPath)
  useEffect(() => {
    if (hydratedRef.current) return
    const raw = (formRaw as any[]) || []
    if (!raw.length) return
    const normalized: Item[] = raw.map((x: any) =>
      typeof x === 'string' ? { id: genId(), value: x } : { id: x.id ?? genId(), value: x.value ?? '' }
    )
    setItems(normalized)
    hydratedRef.current = true
  }, [formRaw])

  useEffect(() => {
    if (!hydratedRef.current) {
      if (items.length === 0) return
      hydratedRef.current = true
    }
    const nonEmpty = items.filter((i) => (i.value || '').trim())
    setValue(fieldPath, nonEmpty, true)
    if (nonEmpty.length > 0) clearError(fieldPath)
    triggerValidation(fieldPath)
  }, [items, setValue, triggerValidation, clearError, fieldPath])

  const dupMap = useMemo(() => {
    const m = new Map<string, number>()
    for (const it of items) {
      const v = (it.value || '').trim()
      if (!v) continue
      m.set(v, (m.get(v) || 0) + 1)
    }
    return m
  }, [items])

  const isDuplicateAt = useCallback(
    (index: number) => {
      const v = (items[index]?.value || '').trim()
      return !!v && (dupMap.get(v) || 0) > 1
    },
    [items, dupMap]
  )

  const addItem = useCallback(() => {
    setItems((prev) => [...prev, { id: genId(), value: '' }])
  }, [])

  const removeItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const updateItem = useCallback((index: number, value: string) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, value } : it)))
  }, [])

  const addIds = useCallback(
    (rawIds: string[]) => {
      const cleaned = rawIds.map((s) => s.trim()).filter(Boolean)
      if (!cleaned.length) return
      const fromInput = Array.from(new Set(cleaned))
      const existing = new Set(items.map((i) => i.value))
      const unique = fromInput.filter((v) => !existing.has(v))
      if (!unique.length) return
      const newItems = unique.map((v) => ({ id: genId(), value: v }))
      setItems((prev) => [...prev, ...newItems])
    },
    [items]
  )

  const appFromTs = watch('products_cluster_data.app_from_ts') as string
  const appToTs = watch('products_cluster_data.app_to_ts') as string
  useEffect(() => {
    if (!hydratedRef.current) return
    triggerValidation(fieldPath)
  }, [appFromTs, appToTs, triggerValidation, fieldPath])

  return { items, addItem, addIds, removeItem, updateItem, isDuplicateAt, dupMap }
}
